import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiClose } from '@mdi/js'

import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { IconTile } from '../ui/icon-tile'
import { ASSET_TYPE_ICON_NAMES, iconPathFor } from '../../lib/asset-type-icons'
import { assetTypeGradient, type AssetTypeLike } from './AssetTypeTile'

/**
 * The icon picker of the asset-type admin page (spec 2026-09-17-04): a
 * `Command` palette over the registry, each row drawing its own glyph on the
 * type's family gradient, so what you pick is what the tree will show.
 *
 * It offers exactly what this build embeds — `ASSET_TYPE_ICON_NAMES` — because
 * a name outside the registry resolves to nothing and would silently draw the
 * family default. Clearing hands the type back to that default on purpose.
 */
export interface AssetTypeIconPickerProps {
    /** The type being edited — its family decides the tile colour of every row. */
    type: AssetTypeLike
    value?: string | null
    onChange: (next: string) => void | Promise<void>
    disabled?: boolean
}

export function AssetTypeIconPicker({
    type,
    value,
    onChange,
    disabled,
}: AssetTypeIconPickerProps) {
    const { t } = useTranslation(['types', 'common'])
    const [open, setOpen] = useState(false)
    const gradient = useMemo(() => assetTypeGradient(type), [type])
    const currentPath = iconPathFor(value)

    const pick = async (name: string) => {
        setOpen(false)
        await onChange(name)
    }

    return (
        <div className="flex items-center gap-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={disabled}
                        className="gap-2"
                        data-testid="asset-type-icon-picker-trigger"
                    >
                        <IconTile
                            path={currentPath}
                            gradient={gradient}
                            size="sm"
                            tone={currentPath ? 'solid' : 'soft'}
                        />
                        <span className="font-mono text-xs" data-testid="asset-type-icon-value">
                            {value || t('asset_types.icon_none', 'family default')}
                        </span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                    <Command>
                        <CommandInput
                            autoFocus
                            placeholder={t('asset_types.icon_search', 'Search an icon…')}
                            data-testid="asset-type-icon-picker-input"
                        />
                        <CommandList>
                            <CommandEmpty>
                                {t('asset_types.icon_empty', 'No icon matches')}
                            </CommandEmpty>
                            <CommandGroup>
                                {ASSET_TYPE_ICON_NAMES.map((name) => (
                                    <CommandItem
                                        key={name}
                                        value={name}
                                        onSelect={() => void pick(name)}
                                        data-testid={`asset-type-icon-option-${name}`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <IconTile
                                                path={iconPathFor(name)}
                                                gradient={gradient}
                                                size="sm"
                                            />
                                            <span className="font-mono text-xs">{name}</span>
                                        </span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {value ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={disabled}
                    aria-label={t('common:actions.clear', 'Clear')}
                    title={t('common:actions.clear', 'Clear')}
                    onClick={() => void onChange('')}
                    data-testid="asset-type-icon-clear"
                >
                    <AppIcon path={mdiClose} size="sm" />
                </Button>
            ) : null}
        </div>
    )
}
